'use client';

import type { CSSProperties, JSX, PointerEvent as ReactPointerEvent, SyntheticEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import clsx from 'clsx';
import { Tooltip as TooltipPrimitive } from 'radix-ui';

import { BaseFontFeatureSettings } from 'DesignSystem2/Text';
import Palette from 'DesignSystem2/Theme/Palette';
import Spacing from 'DesignSystem2/Theme/Spacing';

interface TooltipProps {
    children: JSX.Element;
    title: string | JSX.Element;
    /** Controlled: hover, focus and touch no longer open or close it. */
    open?: boolean;
    /** Touch: a clickable child needs a long press (MUI's 700ms) to open, so a tap still clicks it. */
    childComponentIsClickable?: boolean;
    disableHoverListener?: boolean;
    disablePortal?: boolean;
    enterDelay?: number;
    leaveDelay?: number;
    /** Extra inline styles for the tooltip panel. */
    tooltipStyle?: CSSProperties;
    placement?: 'top' | 'bottom' | 'left' | 'right';
    /** px gap between the trigger and the tooltip. Defaults to 4. */
    offset?: number;
    classname?: string;
    onOpen?: () => void;
    onClose?: () => void;
}

// MUI Tooltip's defaults, which callers were written against.
const DEFAULT_ENTER_DELAY = 100;
const LONG_PRESS_DELAY = 700;
const TOUCH_LINGER = 5000;
// Radix closes a tooltip when its trigger is clicked; the click that follows a tap must not undo it.
const TAP_CLICK_GRACE = 600;
// Above MUI modals (1300), as MUI tooltips were; `z-tooltip` (1000) would sit under them.
const Z_INDEX = 1500;

const panelStyle: CSSProperties = {
    backgroundColor: Palette.Fill.Inverse,
    color: Palette.Text.x10,
    fontFeatureSettings: BaseFontFeatureSettings,
    fontSize: '13px',
    lineHeight: '20px',
    letterSpacing: '0.13px',
    fontWeight: 500,
    padding: `${Spacing(1)} ${Spacing(1.5)}`,
    fontFamily: 'var(--font-kalshi-sans)',
    border: `1px solid ${Palette.Fill.x40}`,
    boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.10)',
    borderRadius: '4px',
    overflow: 'hidden',
    maxWidth: '300px',
    overflowWrap: 'break-word',
    zIndex: Z_INDEX,
};

/**
 * DS2 tooltip, on the Radix primitives DS3 uses (was MUI `Tooltip` + Popper). Same props and
 * defaults as before: `enterDelay` (100ms), `leaveDelay`, a tap opening immediately and lingering
 * 5s on touch, controlled `open` ignoring pointer and focus, an empty title never showing, a string
 * title labelling the child, and the child wrapped in a `<span>`.
 */
const TooltipComponent = ({
    children,
    title,
    open: controlledOpen,
    onOpen,
    onClose,
    childComponentIsClickable = false,
    tooltipStyle,
    placement = 'bottom',
    offset = 4,
    classname,
    disableHoverListener = false,
    disablePortal = false,
    enterDelay = DEFAULT_ENTER_DELAY,
    leaveDelay = 0,
}: TooltipProps): JSX.Element => {
    const isControlled = controlledOpen !== undefined;
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
    // As in MUI, an empty title never shows a tooltip (privacy mode blanks some).
    const open = !!title && (isControlled ? controlledOpen : uncontrolledOpen);
    const closeTimer = useRef<number | undefined>(undefined);
    const pressTimer = useRef<number | undefined>(undefined);
    const touchOpenedAt = useRef(0);

    const clearTimers = useCallback(() => {
        window.clearTimeout(closeTimer.current);
        window.clearTimeout(pressTimer.current);
    }, []);
    useEffect(() => clearTimers, [clearTimers]);

    const setOpen = useCallback(
        (next: boolean) => {
            if (isControlled) {
                // Only dismissals (Escape, outside press) reach a controlled tooltip.
                if (!next) onClose?.();
                return;
            }
            setUncontrolledOpen((prev) => {
                if (prev !== next) (next ? onOpen : onClose)?.();
                return next;
            });
        },
        [isControlled, onOpen, onClose],
    );

    const handleOpenChange = useCallback(
        (next: boolean) => {
            if (!next && Date.now() - touchOpenedAt.current < TAP_CLICK_GRACE) return;
            window.clearTimeout(closeTimer.current);
            if (next || !leaveDelay) {
                setOpen(next);
                return;
            }
            closeTimer.current = window.setTimeout(() => setOpen(false), leaveDelay);
        },
        [leaveDelay, setOpen],
    );

    // Radix composes our handlers first and skips its own when the event is default-prevented:
    // that is how hover (pointermove) and focus are switched off without re-implementing them.
    const suppress = (event: SyntheticEvent) => event.preventDefault();

    const handlePointerDown = (event: ReactPointerEvent<HTMLSpanElement>) => {
        if (event.pointerType !== 'touch' || isControlled) return;
        clearTimers();
        const openForTouch = () => {
            touchOpenedAt.current = Date.now();
            setOpen(true);
            closeTimer.current = window.setTimeout(() => setOpen(false), TOUCH_LINGER);
        };
        if (childComponentIsClickable) pressTimer.current = window.setTimeout(openForTouch, LONG_PRESS_DELAY);
        else openForTouch();
    };
    const handlePointerUp = (event: ReactPointerEvent<HTMLSpanElement>) => {
        if (event.pointerType === 'touch') window.clearTimeout(pressTimer.current);
    };

    const content = (
        <TooltipPrimitive.Content
            side={placement}
            sideOffset={offset}
            // MUI's Popper kept tooltips 2px inside the viewport edge.
            collisionPadding={2}
            style={{ ...panelStyle, ...tooltipStyle }}
            className="data-[state=delayed-open]:animate-quick-fade-in data-[state=instant-open]:animate-quick-fade-in data-[state=closed]:animate-quick-fade-out"
            onClick={(event) => event.stopPropagation()}
            // A touch-opened tooltip must not close on the same tap's pointerdown.
            onPointerDownOutside={isControlled ? undefined : (event) => event.preventDefault()}
        >
            {title}
        </TooltipPrimitive.Content>
    );

    return (
        <TooltipPrimitive.Provider delayDuration={enterDelay}>
            <TooltipPrimitive.Root open={open} onOpenChange={handleOpenChange}>
                <TooltipPrimitive.Trigger
                    asChild
                    onPointerMove={isControlled || disableHoverListener ? suppress : undefined}
                    onFocus={isControlled ? suppress : undefined}
                    onPointerDown={handlePointerDown}
                    onPointerUp={handlePointerUp}
                >
                    <span
                        className={clsx(classname, 'select-none [&_*]:select-none cursor-pointer')}
                        // MUI labels (rather than describes) the child with a string title.
                        aria-label={typeof title === 'string' && title ? title : undefined}
                    >
                        {children}
                    </span>
                </TooltipPrimitive.Trigger>
                {disablePortal ? content : <TooltipPrimitive.Portal>{content}</TooltipPrimitive.Portal>}
            </TooltipPrimitive.Root>
        </TooltipPrimitive.Provider>
    );
};

export default TooltipComponent;
